/**
 * STEP MOA-LIVE-VOICE-1C-RCA · Vosk 단독 최소 테스트 페이지 (Preview 전용).
 * =============================================================================
 * 목적
 *   대표 지시 (반복 실패 종합 RCA) — 추측성 수정 중단.  LIVEMOA 스튜디오 코드와
 *   **완전히 분리된** Lab 페이지에서 vosk-browser 의 실제 동작을 단계별로 검증.
 *
 * 접근 통제
 *   · 로그인 보호 (App.js 의 ProtectedRoute).
 *   · hostname 이 `*.preview.emergentagent.com` 또는 `localhost` 가 아닐 경우
 *     즉시 404 화면 — Production (livemoa.kr) 접근 금지.
 *
 * 테스트 단계
 *   A) 공식 영문 모델 (ccoreilly 예제) · same-origin 호스팅 — Vosk 자체 OK?
 *   B) 한국어 모델 (현재 라이브 코드가 쓰는 모델) · same-origin 호스팅
 *   C) 마이크 입력 테스트 (A 또는 B 가 READY 성공한 뒤에만 활성화)
 *
 * 수집 로그
 *   · createModel() 성공/실패, Worker 내부 메시지 포함
 *   · 서버 진단 (/api/public/debug/vosk-model-diag) — size / sha256 / 필수파일
 *   · HTTP 응답 (status, content-length, content-type)
 *   · 중복 다운로드 탐지 (Resource Timing API)
 *   · WebAssembly 지원, 메모리 추정, UserAgent
 *
 * 기존 LIVEMOA 코드 (`SellerLiveConsole.js`, `useRamoVoice.js`,
 * `ramoWakeWordVosk.js`) 는 전혀 import 하지 않음.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import api from '@/lib/api';
import { matchWakeWord, matchWakeWordInSegments, WAKE_TEXT_CASES, WAKE_ALIASES } from '@/lib/ramoWakeWordConfig';

const BACKEND = process.env.REACT_APP_BACKEND_URL || '';
const EN_URL = `${BACKEND}/api/public/assets/vosk-en-small.tar.gz`;
const KO_URL = `${BACKEND}/api/public/assets/vosk-ko-small.tar.gz`;

const isPreviewHost = () => {
  try {
    const h = window.location.hostname || '';
    return (
      h.endsWith('.preview.emergentagent.com')
      || h.endsWith('.emergent.host')
      || h === 'localhost'
      || h === '127.0.0.1'
    );
  } catch { return false; }
};

const now = () => new Date().toISOString().slice(11, 23);
const fmtBytes = (n) => {
  if (!Number.isFinite(n)) return String(n);
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(2)} MB`;
};

const Row = ({ k, v, mono = true }) => (
  <div className="flex gap-3 text-xs py-0.5">
    <span className="text-gray-500 w-40 shrink-0">{k}</span>
    <span className={mono ? 'font-mono text-gray-900 dark:text-gray-100 break-all' : ''}>
      {v == null ? '—' : String(v)}
    </span>
  </div>
);

const StageCard = ({ title, state, children }) => {
  const colors = {
    idle: 'bg-gray-50 border-gray-200 dark:bg-gray-900 dark:border-gray-800',
    running: 'bg-amber-50 border-amber-300 dark:bg-amber-950 dark:border-amber-800',
    ready: 'bg-emerald-50 border-emerald-300 dark:bg-emerald-950 dark:border-emerald-800',
    failed: 'bg-rose-50 border-rose-300 dark:bg-rose-950 dark:border-rose-800',
  };
  const label = { idle: 'IDLE', running: 'RUNNING', ready: 'READY', failed: 'FAILED' }[state] || 'IDLE';
  return (
    <section className={`border rounded-xl p-4 ${colors[state] || colors.idle}`}>
      <div className="flex items-center justify-between mb-2">
        <h2 className="text-sm font-bold">{title}</h2>
        <span className="text-xs font-mono px-2 py-0.5 rounded bg-black/5 dark:bg-white/10">{label}</span>
      </div>
      {children}
    </section>
  );
};

// -----------------------------------------------------------------------------
// 단일 모델 테스트 러너 (A, B 공용).
// -----------------------------------------------------------------------------
const useVoskStage = (label, url) => {
  const [state, setState] = useState('idle');     // idle | running | ready | failed
  const [logs, setLogs] = useState([]);
  const [httpInfo, setHttpInfo] = useState(null); // {status, length, type, duration_ms}
  const [diag, setDiag] = useState(null);          // 서버 진단 결과
  const [error, setError] = useState(null);
  const [modelRef, setModelRef] = useState(null);
  const abortRef = useRef(null);

  const push = useCallback((line) => {
    setLogs((prev) => [...prev.slice(-199), `[${now()}] ${line}`]);
    // 콘솔에도 즉시 출력하여 Preview DevTools 에서 바로 확인 가능.
     
    console.log(`[VoskLab:${label}]`, line);
  }, [label]);

  const stop = useCallback(() => {
    try { abortRef.current?.abort?.(); } catch {}
    try { modelRef?.terminate?.(); } catch {}
    setModelRef(null);
  }, [modelRef]);

  const run = useCallback(async () => {
    stop();
    setLogs([]); setError(null); setHttpInfo(null); setDiag(null);
    setState('running');
    const t0 = performance.now();
    push(`▶ 시작 · URL=${url}`);

    // 1) 서버 진단 — 파일 무결성 / 내부 구조.
    try {
      const diagName = label === 'EN' ? 'en' : 'ko';
      push(`① 서버 진단 호출 · /api/public/debug/vosk-model-diag?name=${diagName}`);
      const res = await api.get(`/public/debug/vosk-model-diag?name=${diagName}`);
      setDiag(res.data);
      push(`① OK · size=${fmtBytes(res.data.size)} · required=${res.data.all_required_present} · appledouble=${res.data.appledouble_entries}`);
      if (!res.data.all_required_present) {
        push(`⚠️ 필수 파일 누락: ${JSON.stringify(res.data.required_files)}`);
      }
    } catch (e) {
      push(`① FAIL · 서버 진단 실패: ${e?.message || e}`);
    }

    // 2) HEAD/GET 응답 메타 수집 (중복 다운로드 탐지에 사용).
    const ctrl = new AbortController();
    abortRef.current = ctrl;
    try {
      push(`② HEAD 요청`);
      const headRes = await fetch(url, { method: 'HEAD', cache: 'no-store', signal: ctrl.signal });
      push(`② HEAD status=${headRes.status} length=${headRes.headers.get('content-length')} type=${headRes.headers.get('content-type')}`);
      setHttpInfo({
        head_status: headRes.status,
        length: Number(headRes.headers.get('content-length')) || null,
        type: headRes.headers.get('content-type'),
      });
    } catch (e) {
      push(`② HEAD 실패 (무시 가능): ${e?.message || e}`);
    }

    // 3) createModel 호출 — Vosk 내부에서 fetch + tar 해제 + WASM 로드.
    try {
      push(`③ import('vosk-browser') — 번들 로드`);
      const vosk = await import('vosk-browser');
      // RCA probe 용 전역 노출 (Preview Lab 전용 · Production 라우트에서는 자체 404).
      if (typeof window !== 'undefined') window.__vosk = vosk;
      push(`③ import OK · keys=${Object.keys(vosk).join(',')}`);

      push(`④ createModel('${url}') — Worker 생성 · XHR · tar 해제 · WASM 초기화`);
      // Resource Timing baseline — 호출 전 네트워크 항목 수 기록.
      const beforeEntries = performance.getEntriesByType('resource')
        .filter((r) => r.name.includes(url.split('/').pop())).length;

      const model = await vosk.createModel(url);
      push(`④ createModel OK · typeof=${typeof model} · modelProto.KaldiRecognizer=${typeof model?.KaldiRecognizer}`);

      // 중복 다운로드 검증.
      const afterEntries = performance.getEntriesByType('resource')
        .filter((r) => r.name.includes(url.split('/').pop()));
      const newDownloads = afterEntries.length - beforeEntries;
      const totalBytes = afterEntries.reduce((s, r) => s + (r.transferSize || 0), 0);
      push(`⑤ 네트워크 검증 · 해당 URL 리소스 수=${afterEntries.length} (createModel 중 추가=${newDownloads}) · 누적 transfer=${fmtBytes(totalBytes)}`);

      setModelRef(model);
      setState('ready');
      push(`✅ READY · 총 소요 ${(performance.now() - t0).toFixed(0)}ms`);
    } catch (e) {
      const msg = e?.message || String(e);
      const code = e?.code || e?.name || 'unknown';
      setError({ code, message: msg, stack: e?.stack });
      push(`❌ createModel 실패 · code=${code} · msg=${msg}`);
      if (e?.stack) push(`❌ stack: ${e.stack.split('\n').slice(0, 5).join(' | ')}`);
      // Vosk Worker 내부 에러를 찾기 위해 cause 체인 추출.
      let c = e?.cause; let depth = 0;
      while (c && depth < 5) {
        push(`   ↳ cause[${depth}] ${c?.message || c}`);
        c = c?.cause; depth += 1;
      }
      setState('failed');
    }
  }, [url, label, push, stop]);

  return { state, logs, httpInfo, diag, error, run, stop, modelRef };
};

export default function VoskLab({ user }) {
  const preview = useMemo(isPreviewHost, []);

  // UA / WASM / 메모리 환경 정보.
  const [env] = useState(() => {
    const ua = navigator.userAgent || '';
    const mem = navigator.deviceMemory || null;
    const cores = navigator.hardwareConcurrency || null;
    const wasm = typeof WebAssembly !== 'undefined';
    const audio = typeof window.AudioContext !== 'undefined' || typeof window.webkitAudioContext !== 'undefined';
    const workers = typeof Worker !== 'undefined';
    const mic = !!navigator.mediaDevices?.getUserMedia;
    return { ua, mem, cores, wasm, audio, workers, mic };
  });

  const stageEN = useVoskStage('EN', EN_URL);
  const stageKO = useVoskStage('KO', KO_URL);

  // 마이크 단독 테스트 — stage A/B 가 READY 된 경우에만.
  const [micState, setMicState] = useState('idle');
  const [micLogs, setMicLogs] = useState([]);
  const micRefs = useRef({});
  const anyReady = stageEN.state === 'ready' || stageKO.state === 'ready';

  const pushMic = useCallback((line) => {
    setMicLogs((prev) => [...prev.slice(-99), `[${now()}] ${line}`]);
    // eslint-disable-next-line no-console
    console.log('[VoskLab:MIC]', line);
  }, []);

  const startMic = useCallback(async () => {
    const activeStage = stageKO.state === 'ready' ? stageKO : stageEN;
    const activeLabel = stageKO.state === 'ready' ? 'KO' : 'EN';
    if (!activeStage.modelRef) return;
    setMicLogs([]); setMicState('running');
    pushMic(`▶ 마이크 테스트 시작 · 모델=${activeLabel}`);
    try {
      const vosk = await import('vosk-browser');
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: { channelCount: 1, sampleRate: 16000, echoCancellation: true, noiseSuppression: true },
      });
      pushMic(`getUserMedia OK · tracks=${stream.getAudioTracks().length}`);
      const Ctx = window.AudioContext || window.webkitAudioContext;
      const ctx = new Ctx({ sampleRate: 16000 });
      if (ctx.state === 'suspended') await ctx.resume();
      pushMic(`AudioContext sampleRate=${ctx.sampleRate} state=${ctx.state}`);
      // 공식 API (vosk-browser@0.0.8 README · model.d.ts) · model 인스턴스의 KaldiRecognizer getter.
      // 운영 코드 `ramoWakeWordVosk.js:211` 은 `new vosk.KaldiRecognizer(model, 16000)` 사용 중 —
      // vosk 모듈 top-level 에는 KaldiRecognizer 가 없어 TypeError.
      const model = activeStage.modelRef;
      const recognizer = new model.KaldiRecognizer(16000);
      recognizer.setWords(true);
      recognizer.on('result', (m) => pushMic(`result: ${m?.result?.text || JSON.stringify(m)}`));
      recognizer.on('partialresult', (m) => {
        const p = m?.result?.partial || '';
        if (p) pushMic(`partial: ${p}`);
      });
      const src = ctx.createMediaStreamSource(stream);
      const proc = ctx.createScriptProcessor(8192, 1, 1);
      proc.onaudioprocess = (ev) => {
        try { recognizer.acceptWaveform(ev.inputBuffer); } catch {}
      };
      src.connect(proc);
      proc.connect(ctx.destination);
      micRefs.current = { stream, ctx, recognizer, src, proc };
      pushMic(`✅ 마이크 파이프라인 가동 중 — 아무 말이나 해보세요`);
    } catch (e) {
      pushMic(`❌ 마이크 실패: ${e?.name}: ${e?.message || e}`);
      setMicState('failed');
    }
  }, [stageEN, stageKO, pushMic]);

  const stopMic = useCallback(() => {
    try { micRefs.current.proc?.disconnect?.(); } catch {}
    try { micRefs.current.src?.disconnect?.(); } catch {}
    try { micRefs.current.ctx?.close?.(); } catch {}
    try { micRefs.current.stream?.getTracks?.().forEach((t) => t.stop()); } catch {}
    try { micRefs.current.recognizer?.remove?.(); } catch {}
    micRefs.current = {};
    pushMic('■ 중단됨');
    setMicState('idle');
  }, [pushMic]);

  useEffect(() => () => {
    try { stageEN.stop(); } catch {}
    try { stageKO.stop(); } catch {}
    try { stopMic(); } catch {}
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // =========================================================================
  // Phase 2b · 실제 한국어 fixture + 사용자 녹음/업로드 + 오인식 검증.
  //  · WAKE_STRICT: 운영 코드 정규식 (ramoWakeWordVosk.js _WAKE_RE 완전 일치).
  //    PASS 는 오직 STRICT 매칭만.  이전의 PREFIX tolerance 는 **오인식 위험이
  //    크다는 대표 지시에 따라 제거** · "유사 음소 관찰용" 정보로만 노출.
  //  · PIPELINE_OK: Vosk 가 빈 문자열이 아닌 한국어 결과를 1 개 이상 반환했는지.
  // =========================================================================
  // STEP WAKEWORD-FLEX-1 · 운영과 동일한 matchWakeWord() 공통 함수로 판정 (구간별).
  const WAKE_STRICT = {
    test: (combined) => matchWakeWordInSegments(String(combined || '').split(' | ')).matched,
    alias: (combined) => matchWakeWordInSegments(String(combined || '').split(' | ')).alias,
  };
  // informational only — PASS 로 승격 금지.
  const WAKE_NEARMISS = /(라모|라몬|라마|라묘|라므|야마|야머|야무|rammo|ramo)/i;
  const [fixResults, setFixResults] = useState({});
  const [fixRunning, setFixRunning] = useState(null);

  const runFixture = useCallback(async (name, label) => {
    if (stageKO.state !== 'ready' || !stageKO.modelRef) return;
    setFixRunning(name);
    setFixResults((p) => ({ ...p, [name]: { logs: [`▶ ${label} 시작`] } }));
    const log = (line) => setFixResults((p) => ({
      ...p,
      [name]: { ...(p[name] || {}), logs: [...(p[name]?.logs || []), `[${now()}] ${line}`] },
    }));
    const isFP = name.startsWith('fp_');
    try {
      const stem = isFP ? name.slice(3) : name;
      const route = isFP ? 'vosk-fp-audio' : 'vosk-test-audio';
      const url = `${BACKEND}/api/public/debug/${route}/${stem}`;
      log(`fetch ${url}`);
      const res = await fetch(url, { cache: 'no-store' });
      log(`HTTP ${res.status} · bytes=${res.headers.get('content-length')} · type=${res.headers.get('content-type')}`);
      const ab = await res.arrayBuffer();
      const t0Decode = performance.now();
      const Ctx = window.AudioContext || window.webkitAudioContext;
      const ctx = new Ctx({ sampleRate: 16000 });
      const audioBuf = await ctx.decodeAudioData(ab.slice(0));
      log(`decoded · sr=${audioBuf.sampleRate} · ch=${audioBuf.numberOfChannels} · dur=${audioBuf.duration.toFixed(2)}s`);

      // 안전하게 Model 인스턴스에서 공식 API 로 recognizer 생성.
      const model = stageKO.modelRef;
      const recognizer = new model.KaldiRecognizer(16000);
      recognizer.setWords(true);
      const partials = [];
      const results = [];
      recognizer.on('result', (m) => {
        const t = m?.result?.text || '';
        results.push(t);
        log(`result: "${t}"`);
      });
      recognizer.on('partialresult', (m) => {
        const t = m?.result?.partial || '';
        if (t) { partials.push(t); log(`partial: "${t}"`); }
      });

      // mono 추출 · 필요 시 16kHz 로 선형 다운샘플 (WAV 는 이미 16k 이지만 안전).
      const src = audioBuf.getChannelData(0);
      const SR_IN = audioBuf.sampleRate;
      let input16k;
      if (SR_IN === 16000) {
        input16k = src;
      } else {
        const ratio = SR_IN / 16000;
        const outLen = Math.floor(src.length / ratio);
        input16k = new Float32Array(outLen);
        for (let i = 0; i < outLen; i += 1) input16k[i] = src[Math.floor(i * ratio)];
        log(`resampled ${SR_IN} → 16000 · len=${outLen}`);
      }
      // 1024-sample 청크로 공급 (AudioWorklet 유사).
      const CHUNK = 1024;
      // 앞뒤 0.4s silence 패딩으로 Vosk endpoint detection 트리거.
      const PAD = new Float32Array(16000 * 0.4);
      for (let i = 0; i < PAD.length; i += CHUNK) {
        const s = PAD.subarray(i, Math.min(i + CHUNK, PAD.length));
        recognizer.acceptWaveformFloat(s, 16000);
      }
      for (let i = 0; i < input16k.length; i += CHUNK) {
        const slice = input16k.subarray(i, Math.min(i + CHUNK, input16k.length));
        recognizer.acceptWaveformFloat(slice, 16000);
      }
      for (let i = 0; i < PAD.length; i += CHUNK) {
        const s = PAD.subarray(i, Math.min(i + CHUNK, PAD.length));
        recognizer.acceptWaveformFloat(s, 16000);
      }
      recognizer.retrieveFinalResult();
      await new Promise((r) => setTimeout(r, 2500));

      const combinedAll = [...results, ...partials].filter(Boolean).join(' | ');
      const strict = WAKE_STRICT.test(combinedAll);
      const nearmiss = WAKE_NEARMISS.test(combinedAll);
      const pipelineOk = results.some((t) => t && t.trim())
        || partials.some((t) => t && t.trim());
      // PASS 는 오직 strict 만 — 오인식 위험 방지.
      const matched = strict;
      const elapsed = Math.round(performance.now() - t0Decode);
      log(`──────── 종합 (경과 ${elapsed}ms) ────────`);
      log(`results = ${JSON.stringify(results)}`);
      log(`partials.tail = ${JSON.stringify(partials.slice(-5))}`);
      log(`WAKE_STRICT = ${strict} · WAKE_NEARMISS(informational) = ${nearmiss} · PIPELINE_OK = ${pipelineOk}`);
      log(`Vosk 원본 전사 = "${combinedAll}" · 매칭 별칭 = ${WAKE_STRICT.alias(combinedAll) || '(없음)'}`);
      if (strict) log(`✅ 호출어 인식 PASS (matchWakeWord 매칭)`);
      else if (nearmiss) log(`❌ FAIL — strict 미매칭 (유사 음소 ${combinedAll.match(WAKE_NEARMISS)?.[0]} 관찰됨)`);
      else if (pipelineOk) log(`❌ FAIL — strict 미매칭 (한국어 파이프라인은 OK)`);
      else log(`❌ FAIL (결과 없음)`);

      try { recognizer.remove(); } catch {}
      try { ctx.close(); } catch {}
      setFixResults((p) => ({
        ...p,
        [name]: {
          ...(p[name] || {}),
          matched, strict, nearmiss, pipelineOk, elapsed, isFP,
          combined: combinedAll,
          results, partials: partials.slice(-10),
          logs: p[name]?.logs,
        },
      }));
    } catch (e) {
      log(`❌ 예외: ${e?.name}: ${e?.message || e}`);
    } finally {
      setFixRunning(null);
    }
  }, [stageKO]);

  if (!preview) {
    return (
      <div className="min-h-screen bg-white dark:bg-[#0A0A0A] flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-3xl font-black mb-2">404</h1>
          <p className="text-sm text-gray-500">페이지를 찾을 수 없어요.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white dark:bg-[#0A0A0A] text-gray-900 dark:text-gray-100">
      <div className="max-w-5xl mx-auto px-5 py-8" data-testid="vosk-lab-root">
        <header className="mb-6">
          <div className="text-xs font-mono text-amber-700 dark:text-amber-400 mb-1">
            PREVIEW ONLY · MOA-LIVE-VOICE-1C RCA · Phase 1
          </div>
          <h1 className="text-2xl font-black">Vosk 단독 최소 테스트 (Lab)</h1>
          <p className="text-sm text-gray-500 mt-1">
            LIVEMOA 스튜디오 코드 접촉 금지.  Vosk 자체 동작 → 영문/한국어 모델 비교 → 마이크 분리 검증.
          </p>
        </header>

        <section className="mb-6 border rounded-xl p-4 bg-gray-50 dark:bg-gray-900 border-gray-200 dark:border-gray-800">
          <h2 className="text-sm font-bold mb-2">환경 정보</h2>
          <Row k="user" v={user?.email || user?.id || '(logged in)'} />
          <Row k="host" v={typeof window !== 'undefined' ? window.location.hostname : '—'} />
          <Row k="WebAssembly" v={env.wasm ? 'OK' : 'MISSING'} />
          <Row k="AudioContext" v={env.audio ? 'OK' : 'MISSING'} />
          <Row k="Worker" v={env.workers ? 'OK' : 'MISSING'} />
          <Row k="getUserMedia" v={env.mic ? 'OK' : 'MISSING'} />
          <Row k="deviceMemory(GB)" v={env.mem} />
          <Row k="hardwareConcurrency" v={env.cores} />
          <Row k="userAgent" v={env.ua} />
        </section>

        <div className="grid gap-4 md:grid-cols-2">
          <StageCard title="A) 공식 영문 모델 (40MB)" state={stageEN.state}>
            <div className="flex gap-2 mb-3">
              <button
                data-testid="vosk-lab-en-run"
                className="px-3 py-1.5 rounded bg-black text-white text-xs font-bold disabled:opacity-50"
                onClick={stageEN.run}
                disabled={stageEN.state === 'running'}
              >
                createModel 실행
              </button>
              <button
                data-testid="vosk-lab-en-stop"
                className="px-3 py-1.5 rounded border text-xs"
                onClick={stageEN.stop}
              >중단</button>
            </div>
            {stageEN.diag && (
              <div className="mb-2">
                <Row k="file.size" v={fmtBytes(stageEN.diag.size)} />
                <Row k="sha256" v={stageEN.diag.sha256?.slice(0, 20) + '…'} />
                <Row k="required_files" v={JSON.stringify(stageEN.diag.required_files)} />
                <Row k="appledouble" v={stageEN.diag.appledouble_entries} />
              </div>
            )}
            {stageEN.error && (
              <div className="mb-2 text-xs">
                <Row k="error.code" v={stageEN.error.code} />
                <Row k="error.msg" v={stageEN.error.message} />
              </div>
            )}
            <LogView logs={stageEN.logs} testid="vosk-lab-en-logs" />
          </StageCard>

          <StageCard title="B) 한국어 모델 (83MB · 현재 운영)" state={stageKO.state}>
            <div className="flex gap-2 mb-3">
              <button
                data-testid="vosk-lab-ko-run"
                className="px-3 py-1.5 rounded bg-black text-white text-xs font-bold disabled:opacity-50"
                onClick={stageKO.run}
                disabled={stageKO.state === 'running'}
              >
                createModel 실행
              </button>
              <button
                data-testid="vosk-lab-ko-stop"
                className="px-3 py-1.5 rounded border text-xs"
                onClick={stageKO.stop}
              >중단</button>
            </div>
            {stageKO.diag && (
              <div className="mb-2">
                <Row k="file.size" v={fmtBytes(stageKO.diag.size)} />
                <Row k="sha256" v={stageKO.diag.sha256?.slice(0, 20) + '…'} />
                <Row k="required_files" v={JSON.stringify(stageKO.diag.required_files)} />
                <Row k="appledouble" v={stageKO.diag.appledouble_entries} />
              </div>
            )}
            {stageKO.error && (
              <div className="mb-2 text-xs">
                <Row k="error.code" v={stageKO.error.code} />
                <Row k="error.msg" v={stageKO.error.message} />
              </div>
            )}
            <LogView logs={stageKO.logs} testid="vosk-lab-ko-logs" />
          </StageCard>
        </div>

        <section className="mt-4 border rounded-xl p-4 bg-sky-50 dark:bg-sky-950 border-sky-200 dark:border-sky-900">
          <div className="flex items-center justify-between mb-2">
            <h2 className="text-sm font-bold">C) 마이크 입력 테스트</h2>
            <span className="text-xs text-gray-500">
              {anyReady ? '모델 READY — 활성화됨' : '모델 READY 대기 중'}
            </span>
          </div>
          <div className="flex gap-2 mb-3">
            <button
              data-testid="vosk-lab-mic-start"
              className="px-3 py-1.5 rounded bg-sky-600 text-white text-xs font-bold disabled:opacity-40"
              onClick={startMic}
              disabled={!anyReady || micState === 'running'}
            >마이크 시작</button>
            <button
              data-testid="vosk-lab-mic-stop"
              className="px-3 py-1.5 rounded border text-xs"
              onClick={stopMic}
            >중단</button>
          </div>
          <LogView logs={micLogs} testid="vosk-lab-mic-logs" />
        </section>

        {/* Phase 2 · 한국어 TTS WAV fixture 로 호출어 regex 매칭 end-to-end 검증 */}
        <section
          data-testid="vosk-lab-fixture-section"
          className="mt-4 border rounded-xl p-4 bg-violet-50 dark:bg-violet-950 border-violet-200 dark:border-violet-900"
        >
          <div className="flex items-center justify-between mb-2">
            <h2 className="text-sm font-bold">D) 한국어 호출어 fixture (TTS — Vosk 파이프라인 검증)</h2>
            <span className="text-xs text-gray-500">
              {stageKO.state === 'ready' ? 'KO 모델 READY' : 'READY 대기 중'}
            </span>
          </div>
          <p className="text-[11px] text-gray-500 mb-3">
            PASS 조건: 운영 공통 함수 `matchWakeWord()` 매칭.  TTS "라모야" 미매칭은
            TTS 발음 한계이며 사람 녹음은 F) 섹션에서 검증합니다.
            NEARMISS 는 informational only (PASS 로 승격 금지).
          </p>
          <div className="grid gap-3 md:grid-cols-4">
            {[
              { k: 'ko_positive', label: '✓ 다음 상품 보여줘 (positive control)' },
              { k: 'ramoya_only', label: '라모야 (단독 · TTS)' },
              { k: 'ramoya_next_cont', label: '라모야 다음 상품 (연속 · TTS)' },
              { k: 'ramoya_next_sep', label: '라모야 … 다음 상품 (분리 · TTS)' },
            ].map(({ k, label }) => {
              const r = fixResults[k];
              const stateLabel = fixRunning === k ? 'RUNNING'
                : r?.strict === true ? 'PASS'
                : r?.strict === false ? 'FAIL'
                : 'IDLE';
              const color = stateLabel === 'PASS' ? 'bg-emerald-100 border-emerald-300 dark:bg-emerald-900/40'
                : stateLabel === 'FAIL' ? 'bg-rose-100 border-rose-300 dark:bg-rose-900/40'
                : stateLabel === 'RUNNING' ? 'bg-amber-100 border-amber-300 dark:bg-amber-900/40'
                : 'bg-white/60 dark:bg-black/30 border-gray-200 dark:border-gray-800';
              return (
                <div key={k} className={`border rounded-lg p-2 ${color}`}>
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs font-bold">{label}</span>
                    <span className="text-[10px] font-mono px-1.5 py-0.5 rounded bg-black/5 dark:bg-white/10">{stateLabel}</span>
                  </div>
                  <button
                    data-testid={`vosk-lab-fixture-${k}`}
                    className="w-full px-2 py-1 rounded bg-violet-600 text-white text-[11px] font-bold disabled:opacity-40 mb-2"
                    onClick={() => runFixture(k, label)}
                    disabled={stageKO.state !== 'ready' || fixRunning !== null}
                  >실행</button>
                  <LogView logs={r?.logs || []} testid={`vosk-lab-fixture-${k}-logs`} />
                </div>
              );
            })}
          </div>
        </section>

        {/* Phase 2b · 오인식 (false-positive) 검증 — 일반 방송 멘트는 STRICT 비매칭이어야 함 */}
        <section
          data-testid="vosk-lab-fp-section"
          className="mt-4 border rounded-xl p-4 bg-rose-50 dark:bg-rose-950 border-rose-200 dark:border-rose-900"
        >
          <div className="flex items-center justify-between mb-2">
            <h2 className="text-sm font-bold">E) 오인식 (False-Positive) 검증</h2>
            <span className="text-xs text-gray-500">일반 방송 멘트 · strict 비매칭 = PASS</span>
          </div>
          <p className="text-[11px] text-gray-500 mb-3">
            일반 LIVE 방송 멘트 (인사말 · 가격 · 유사발음 '라면/라마단' 등) 가
            운영 strict regex 와 **매칭되지 않아야** 합니다.  매칭되면 호출어가
            오탐지되는 심각한 결함.
          </p>
          <div className="grid gap-3 md:grid-cols-3">
            {[
              { k: 'fp_greeting', label: '안녕하세요 … 라이브 방송' },
              { k: 'fp_promo',    label: '인기 상품 품절주의' },
              { k: 'fp_price',    label: '가격 삼만 구천원 …' },
              { k: 'fp_near_wake',label: '라면 라마단 …' },
              { k: 'fp_random1',  label: '다음 상품도 기대' },
              { k: 'fp_random2',  label: '나무 야무지게 …' },
            ].map(({ k, label }) => {
              const r = fixResults[k];
              // 오인식 PASS 조건: strict === false
              const stateLabel = fixRunning === k ? 'RUNNING'
                : r?.strict === false ? 'PASS'   // 매칭 안 됨 = 안전
                : r?.strict === true ? 'FAIL'    // 매칭 됨 = 오탐지 발생
                : 'IDLE';
              const color = stateLabel === 'PASS' ? 'bg-emerald-100 border-emerald-300 dark:bg-emerald-900/40'
                : stateLabel === 'FAIL' ? 'bg-rose-200 border-rose-400 dark:bg-rose-900/60'
                : stateLabel === 'RUNNING' ? 'bg-amber-100 border-amber-300 dark:bg-amber-900/40'
                : 'bg-white/60 dark:bg-black/30 border-gray-200 dark:border-gray-800';
              return (
                <div key={k} className={`border rounded-lg p-2 ${color}`}>
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs font-bold">{label}</span>
                    <span className="text-[10px] font-mono px-1.5 py-0.5 rounded bg-black/5 dark:bg-white/10">{stateLabel}</span>
                  </div>
                  <button
                    data-testid={`vosk-lab-fixture-${k}`}
                    className="w-full px-2 py-1 rounded bg-rose-600 text-white text-[11px] font-bold disabled:opacity-40 mb-2"
                    onClick={() => runFixture(k, label)}
                    disabled={stageKO.state !== 'ready' || fixRunning !== null}
                  >실행</button>
                  <LogView logs={r?.logs || []} testid={`vosk-lab-fixture-${k}-logs`} />
                </div>
              );
            })}
          </div>
        </section>

        <WakeTextTable />

        {/* Phase 2b · 실제 사람 녹음/업로드 + 반복 측정 (최종 wake-word 품질 검증) */}
        <HumanVoiceSection
          stageKO={stageKO}
          WAKE_STRICT={WAKE_STRICT}
          WAKE_NEARMISS={WAKE_NEARMISS}
        />

        <footer className="mt-8 text-xs text-gray-400">
          Phase 1/2 완료 후 라우트/엔드포인트/fixture 제거 예정 · Production 반영 금지
        </footer>
      </div>
    </div>
  );
}

// STEP WAKEWORD-FLEX-1 · 텍스트 표 테스트 (모델 불필요) + 자유 입력.
const WakeTextTable = () => {
  const [custom, setCustom] = useState('');
  const rows = WAKE_TEXT_CASES.map((c) => ({ ...c, m: matchWakeWord(c.text) }));
  const passCount = rows.filter((r) => r.m.matched === r.expect).length;
  const cm = custom ? matchWakeWord(custom) : null;
  return (
    <section data-testid="vosk-lab-wake-text-section"
             className="mt-4 border rounded-xl p-4 bg-sky-50 dark:bg-sky-950 border-sky-200 dark:border-sky-900">
      <div className="flex items-center justify-between mb-2">
        <h2 className="text-sm font-bold">G) 호출어 텍스트 판정 표 (matchWakeWord)</h2>
        <span className="text-xs font-mono" data-testid="vosk-lab-wake-text-summary">{passCount}/{rows.length} PASS</span>
      </div>
      <p className="text-[11px] text-gray-500 mb-2">별칭: {WAKE_ALIASES.join(', ')} · 단어 단위 일치 · 발화 앞부분(0~2번째 단어) 또는 마지막 단어만 인정.</p>
      <input value={custom} onChange={(e) => setCustom(e.target.value)} placeholder="문장을 입력해 보세요"
             className="w-full mb-1 px-2 py-1 text-xs border rounded bg-white dark:bg-black" data-testid="vosk-lab-wake-text-input" />
      {cm && (
        <div className="text-[11px] mb-2 font-mono" data-testid="vosk-lab-wake-text-custom-result">
          matched={String(cm.matched)} · alias={cm.alias || '-'} · command="{cm.command}"
        </div>
      )}
      <table className="w-full text-[11px]" data-testid="vosk-lab-wake-text-table">
        <thead><tr className="text-left"><th>문장</th><th>기대</th><th>결과</th><th>별칭</th><th>후속 명령</th><th>판정</th></tr></thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={r.text} className="border-t border-sky-200/60" data-testid={`vosk-lab-wake-text-row-${i}`}>
              <td className="py-0.5">{r.text}</td>
              <td>{r.expect ? '호출' : '무시'}</td>
              <td>{r.m.matched ? '호출' : '무시'}</td>
              <td>{r.m.alias || '-'}</td>
              <td>{r.m.command || '-'}</td>
              <td className={r.m.matched === r.expect ? 'text-emerald-600 font-bold' : 'text-rose-600 font-bold'}>
                {r.m.matched === r.expect ? 'PASS' : 'FAIL'}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </section>
  );
};

const LogView = ({ logs, testid }) => (
  <pre
    data-testid={testid}
    className="text-[11px] font-mono bg-white/60 dark:bg-black/40 border border-gray-200 dark:border-gray-800 rounded p-2 max-h-56 overflow-auto whitespace-pre-wrap"
  >
    {logs.length === 0 ? '(no logs yet)' : logs.join('\n')}
  </pre>
);

// =============================================================================
// 실제 사람 녹음 + WAV 업로드 + 반복 측정 섹션.
// Playwright 로 자동 테스트 불가 (실제 마이크 필요).  CEO / 테스터가 Preview
// 에서 직접 수행.  반복 결과를 누적하여 strict 매칭률과 지연시간 통계 제공.
// =============================================================================
const HumanVoiceSection = ({ stageKO, WAKE_STRICT, WAKE_NEARMISS }) => {
  const [rows, setRows] = useState([]); // {source, text, strict, nearmiss, dur_ms, latency_ms}
  const [recording, setRecording] = useState(false);
  const [log, setLog] = useState([]);
  const refs = useRef({});

  const push = (line) => setLog((p) => [...p.slice(-99), `[${now()}] ${line}`]);

  const _runOn = async (source, audioBuf, acquisitionStart) => {
    if (!stageKO.modelRef) { push('모델 미준비 — Phase B KO READY 필요'); return; }
    const t0 = performance.now();
    const recognizer = new stageKO.modelRef.KaldiRecognizer(16000);
    recognizer.setWords(true);
    const partials = []; const results = [];
    let firstEventAt = null;
    recognizer.on('result', (m) => {
      if (firstEventAt === null) firstEventAt = performance.now();
      results.push(m?.result?.text || '');
    });
    recognizer.on('partialresult', (m) => {
      if (firstEventAt === null) firstEventAt = performance.now();
      const t = m?.result?.partial || '';
      if (t) partials.push(t);
    });

    const src = audioBuf.getChannelData(0);
    const SR_IN = audioBuf.sampleRate;
    let s16;
    if (SR_IN === 16000) s16 = src;
    else {
      const ratio = SR_IN / 16000;
      const outLen = Math.floor(src.length / ratio);
      s16 = new Float32Array(outLen);
      for (let i = 0; i < outLen; i += 1) s16[i] = src[Math.floor(i * ratio)];
    }
    const CHUNK = 1024;
    const PAD = new Float32Array(16000 * 0.4);
    const feed = (arr) => { for (let i = 0; i < arr.length; i += CHUNK) recognizer.acceptWaveformFloat(arr.subarray(i, Math.min(i + CHUNK, arr.length)), 16000); };
    feed(PAD); feed(s16); feed(PAD);
    recognizer.retrieveFinalResult();
    await new Promise((r) => setTimeout(r, 2500));

    const combined = [...results, ...partials].filter(Boolean).join(' | ');
    const strict = WAKE_STRICT.test(combined);
    const nearmiss = WAKE_NEARMISS.test(combined);
    const latency = firstEventAt ? Math.round(firstEventAt - acquisitionStart) : null;
    const procMs = Math.round(performance.now() - t0);
    push(`${source} · dur=${audioBuf.duration.toFixed(2)}s · text="${results.join(' ') || partials.slice(-1)[0] || '(none)'}" · strict=${strict} · nearmiss=${nearmiss} · latency=${latency}ms · proc=${procMs}ms`);
    try { recognizer.remove(); } catch {}
    setRows((p) => [...p, {
      source, dur_ms: Math.round(audioBuf.duration * 1000),
      text: results.join(' ') || partials.slice(-1)[0] || '',
      strict, nearmiss, latency_ms: latency, proc_ms: procMs,
      ts: new Date().toISOString().slice(11, 19),
    }]);
  };

  const startRecord = useCallback(async () => {
    if (recording) return;
    setLog([]); push('마이크 open — 2.5s 후 자동 종료');
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: { channelCount: 1, sampleRate: 16000, echoCancellation: true, noiseSuppression: true },
      });
      const rec = new MediaRecorder(stream);
      const chunks = [];
      rec.ondataavailable = (e) => { if (e.data?.size) chunks.push(e.data); };
      const startAt = performance.now();
      rec.start();
      setRecording(true);
      refs.current = { stream, rec };
      await new Promise((r) => setTimeout(r, 2500));
      await new Promise((r) => { rec.onstop = r; rec.stop(); });
      stream.getTracks().forEach((t) => t.stop());
      setRecording(false);
      const blob = new Blob(chunks, { type: rec.mimeType || 'audio/webm' });
      push(`녹음 종료 · ${blob.size} bytes · type=${blob.type}`);
      const ab = await blob.arrayBuffer();
      const ctx = new (window.AudioContext || window.webkitAudioContext)({ sampleRate: 16000 });
      const audioBuf = await ctx.decodeAudioData(ab);
      await _runOn('REC', audioBuf, startAt);
      try { ctx.close(); } catch {}
    } catch (e) {
      push(`❌ 녹음 실패: ${e?.name}: ${e?.message || e}`);
      setRecording(false);
    }
  }, [recording]);  // eslint-disable-line react-hooks/exhaustive-deps

  const onFile = useCallback(async (ev) => {
    const file = ev.target.files?.[0];
    if (!file) return;
    push(`파일 로드 · ${file.name} · ${file.size} bytes · type=${file.type}`);
    const startAt = performance.now();
    const ab = await file.arrayBuffer();
    try {
      const ctx = new (window.AudioContext || window.webkitAudioContext)({ sampleRate: 16000 });
      const audioBuf = await ctx.decodeAudioData(ab);
      await _runOn(`UP:${file.name}`, audioBuf, startAt);
      try { ctx.close(); } catch {}
    } catch (e) {
      push(`❌ 디코딩 실패: ${e?.message || e}`);
    }
    ev.target.value = '';
  }, []);  // eslint-disable-line react-hooks/exhaustive-deps

  const strictCount = rows.filter((r) => r.strict).length;
  const nearmissCount = rows.filter((r) => !r.strict && r.nearmiss).length;
  const latencies = rows.map((r) => r.latency_ms).filter((v) => v != null).sort((a, b) => a - b);
  const p50 = latencies[Math.floor(latencies.length * 0.5)];
  const p95 = latencies[Math.floor(latencies.length * 0.95)];

  return (
    <section
      data-testid="vosk-lab-human-section"
      className="mt-4 border rounded-xl p-4 bg-sky-50 dark:bg-sky-950 border-sky-200 dark:border-sky-900"
    >
      <div className="flex items-center justify-between mb-2">
        <h2 className="text-sm font-bold">F) 실제 사람 녹음 / WAV 업로드 (최종 wake-word 품질 검증)</h2>
        <span className="text-xs text-gray-500">
          {stageKO.state === 'ready' ? 'KO 모델 READY' : 'READY 대기 중'}
        </span>
      </div>
      <p className="text-[11px] text-gray-500 mb-3">
        2.5s 녹음 또는 WAV 업로드 → 운영 strict regex 매칭 · 지연시간(ms) · 반복 집계.
        최소 2 명 이상 화자 × 수회 반복 발화를 권장.  "라모야" 단독 · "라모야 → 다음 상품"
        분리 · "라모야 다음 상품" 연속 각각 측정.  PASS 는 strict 만.
      </p>
      <div className="flex flex-wrap gap-2 mb-3">
        <button
          data-testid="vosk-lab-record-start"
          className="px-3 py-1.5 rounded bg-sky-600 text-white text-xs font-bold disabled:opacity-40"
          onClick={startRecord}
          disabled={stageKO.state !== 'ready' || recording}
        >{recording ? '녹음 중…' : '녹음 (2.5s)'}</button>
        <label className="px-3 py-1.5 rounded bg-sky-500 text-white text-xs font-bold cursor-pointer">
          WAV 업로드
          <input
            data-testid="vosk-lab-upload-input"
            type="file"
            accept="audio/wav,audio/mpeg,audio/ogg,audio/webm"
            className="hidden"
            onChange={onFile}
          />
        </label>
        <button
          data-testid="vosk-lab-rows-clear"
          className="px-3 py-1.5 rounded border text-xs"
          onClick={() => setRows([])}
        >결과 초기화</button>
      </div>
      <div className="text-[11px] mb-2 font-mono text-gray-700 dark:text-gray-300">
        n={rows.length} · strict PASS={strictCount} · nearmiss={nearmissCount} ·
        latency p50={p50 ?? '—'}ms p95={p95 ?? '—'}ms
      </div>
      <div className="overflow-auto max-h-60 border rounded bg-white/60 dark:bg-black/30">
        <table className="w-full text-[11px] font-mono">
          <thead className="bg-black/5 dark:bg-white/10">
            <tr><th className="text-left p-1">#</th><th className="text-left p-1">ts</th><th className="text-left p-1">source</th><th className="text-left p-1">text</th><th className="text-right p-1">dur</th><th className="text-right p-1">latency</th><th className="text-center p-1">strict</th><th className="text-center p-1">near</th></tr>
          </thead>
          <tbody data-testid="vosk-lab-rows-tbody">
            {rows.map((r, i) => (
              <tr key={i} className={r.strict ? 'bg-emerald-50 dark:bg-emerald-900/20' : 'odd:bg-black/5 dark:odd:bg-white/5'}>
                <td className="p-1">{i + 1}</td>
                <td className="p-1">{r.ts}</td>
                <td className="p-1">{r.source}</td>
                <td className="p-1 break-all">{r.text || '(empty)'}</td>
                <td className="p-1 text-right">{r.dur_ms}ms</td>
                <td className="p-1 text-right">{r.latency_ms ?? '—'}ms</td>
                <td className="p-1 text-center">{r.strict ? '✅' : '❌'}</td>
                <td className="p-1 text-center">{r.nearmiss ? 'o' : '-'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <LogView logs={log} testid="vosk-lab-human-logs" />
    </section>
  );
};
